import * as _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import { ChartEntity, DataDescriptorEntity, Enterprise, EnterpriseAppState } from "@ctra/api";
import { AntDesignSelect as Select, Col, Row, Typography, Button } from "@ctra/components";
import { defaultLocale, Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Optional } from "@ctra/utils";
import { useGoogleAnalytics } from "@ctra/analytics";

import { useFarm } from "@farms";
import { useEvent } from "@events";
import { useChartFilters } from "@chart";
import { useDataDictionary } from "@base";

import { GAActions } from "../../../analytics";
import { closestSupportedInterval } from "../CorrelationList";

const { Option, OptGroup } = Select;
const { Text } = Typography;

/**
 * Chart picker component
 * @param {(value: DataDescriptorEntity["id"]) => void} onChange
 * @param {string | undefined} defaultValue
 * @param {boolean | undefined} showLabel
 * @param {boolean | undefined} showButton
 * @param {Array<DataDescriptorEntity["id"]> | undefined} filterValues
 * @return {JSX.Element}
 * @constructor
 */
export const ChartPicker: FC<{
  onChange: (value: DataDescriptorEntity["id"]) => void;
  defaultValue?: DataDescriptorEntity["id"];
  showLabel?: boolean;
  showButton?: boolean;
  filterValues?: Array<DataDescriptorEntity["id"]>;
}> = ({ onChange, defaultValue, showLabel = true, showButton = true, filterValues }) => {
  const [filter, setFilter] = useState<string>("");
  const [value, setValue] = useState<DataDescriptorEntity["id"]>();
  const { farm } = useFarm();
  const { t } = useTranslation();
  const { isoDuration } = useChartFilters();
  const { trackEvent } = useGoogleAnalytics();
  const { search } = useLocation();
  const { event } = useEvent();

  const {
    dataDescriptors,
    subsets: { farm: farmDataDescriptorIDList, eventScoped: eventScopedDataDescriptorList }
  } = useDataDictionary();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const farmID = farm!.id;

  const {
    chart,
    kpi: { category, displayName },
    analytics: {
      correlations: { add, headers },
      compare: {
        dropdowns: { placeholder, suggestions, notFound }
      }
    }
  } = Content;

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const dataDescriptorID = searchParams.get("dataDescriptorID");

    if (dataDescriptorID && dataDescriptorID !== defaultValue) {
      onChange(dataDescriptorID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /**
   * Get the data dictionary for the given farm id / event scope
   * @type {Pick<DataDescriptorList, keyof DataDescriptorList>}
   */
  const dataDictionary = _.pick(
    dataDescriptors,
    event ? eventScopedDataDescriptorList : farmDataDescriptorIDList
  );

  /**
   * List of descriptors that have suggestions enabled
   * @type {Optional<Array<DataDescriptorEntity["id"]>>}
   */
  const descriptorsWithCorrelations = useSelector<
    EnterpriseAppState,
    Optional<Array<DataDescriptorEntity["id"]>>
  >((state) =>
    Enterprise.entities.getSupportedDescriptorsForInterval(state, {
      interval: closestSupportedInterval(isoDuration),
      farmID
    })
  );

  /**
   * Get a collection of default charts from the data descriptors (to be used for their titles)
   */
  const charts = useSelector<EnterpriseAppState, Record<DataDescriptorEntity["id"], ChartEntity>>((state) =>
    _.reduce(
      dataDictionary,
      (result, { id, supportedCharts }) => {
        result[id] = Enterprise.entities.getChart(state, {
          id: _.head(supportedCharts) as ChartEntity["id"]
        }) as ChartEntity;

        return result;
      },
      {} as Record<DataDescriptorEntity["id"], ChartEntity>
    )
  );

  /**
   * Check if the descriptor (combined descriptor) has correlations enabled
   * @param {DataDescriptorEntity["id"]} dataDescriptorID
   * @return {boolean}
   */
  const hasCorrelations = (dataDescriptorID: DataDescriptorEntity["id"]) => {
    /**
     * Pick the combined descriptors from the data dictionary
     * @type {any}
     */
    const combinedDescriptors = _.get(
      dataDictionary,
      [dataDescriptorID, "dataProperties", "combinedDescriptors"],
      []
    );

    return !_.isEmpty(
      _.intersection(descriptorsWithCorrelations, [...combinedDescriptors, dataDescriptorID])
    );
  };

  /**
   * Get the dropdown data
   * @type {unknown[]}
   */
  const items = _.map(
    _.groupBy(dataDictionary, ({ dataProperties: { category } }) => category),
    (list, groupName) => ({
      key: groupName,
      label: t<string>(category(groupName)),
      options: _.map(list, ({ id, dataProperties: { typeName } }) => ({
        key: id,
        value: id,
        label: t<string>(displayName(typeName), {
          case: null,
          variant: "short",
          makeDefaultValue: true
        })
      }))
    })
  );

  /**
   * Filter the options based on search query
   */
  const searchFiltered = items.reduce((acc: typeof items, data) => {
    const filteredGroupOptions = data.options.filter((option) =>
      option.label.toLowerCase().includes(filter.toLowerCase())
    );

    if (filteredGroupOptions.length > 0) {
      acc.push({
        ...data,
        options: filteredGroupOptions
      });
    }

    return acc;
  }, []);

  return (
    <Row className="ant-form-item-row" gutter={16}>
      {showLabel && (
        <Col span={24} className="ant-form-item-label">
          {t<string>(headers.chartSelect)}
        </Col>
      )}
      <Col className="ant-form-item-control" flex={1}>
        <Select
          style={{ width: "100%" }}
          placeholder={t<string>(placeholder.chart)}
          onSearch={setFilter}
          onChange={event ? onChange : setValue}
          value={value}
          filterOption={false}
          showSearch
          notFoundContent={t(notFound)}
        >
          {searchFiltered.map(({ label, options }) => {
            /**
             * filter the values if props specify any filter values
             */
            const filtered = _.filter(options, ({ value }) => !_.includes(filterValues, value));

            return (
              !_.isEmpty(filtered) && (
                <OptGroup key={label} label={label}>
                  {filtered.map(({ label, value }) => (
                    <Option key={value} value={value}>
                      {hasCorrelations(value) ? (
                        <Row justify="space-between">
                          <Col>{label}</Col>
                          <Col>
                            <Text type="secondary">{t(suggestions)}</Text>
                          </Col>
                        </Row>
                      ) : (
                        label
                      )}
                    </Option>
                  ))}
                </OptGroup>
              )
            );
          })}
        </Select>
      </Col>
      {showButton && (
        <Col>
          <Button
            disabled={!value}
            type="primary"
            onClick={() => {
              if (value) {
                trackEvent(GAActions.selectChart, {
                  label: t<string>(chart.title(charts[value].title), { lng: defaultLocale })
                });

                onChange(value);
                setValue(void 0);
                setFilter("");
              }
            }}
          >
            {t<string>(add)}
          </Button>
        </Col>
      )}
    </Row>
  );
};
